import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import EmployerCommerceShell from '../../components/employer/EmployerCommerceShell';
import { Button, Card, CardBody, CardHeader, Input, Label, Textarea } from '../../components/ui/primitives';
import {
  createCommerceOrder,
  fetchBillingProfile,
  fetchBuildOfferings,
  fetchCommerceBuildRequests,
  fetchCreditBalance,
  type CommerceBillingProfile,
  type CommerceBuildRequest,
  type CommerceBuildRequestStatus,
  type CommerceCheckoutMode,
  type CommerceCreditBalance,
  type CommercePaymentStatus,
  type CommerceOffering,
  useApi,
} from '../../services/api';
import { buildInvoiceDetailsUrl } from '../../services/commerceIntent';
import { rememberPendingCommerceOrder, submitPayFastHostedPayment } from '../../services/payfast';

const currencyFormatter = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const heroPriceFormatter = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value: string | number) => {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? currencyFormatter.format(amount) : 'R0.00';
};

const formatHeroPrice = (value: string | number) => {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? heroPriceFormatter.format(amount) : 'R0';
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const getMetadataObject = (offering: CommerceOffering) => {
  const metadata = offering.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return {} as Record<string, unknown>;
  }
  return metadata;
};

const getMetadataString = (offering: CommerceOffering, key: string) => {
  const value = getMetadataObject(offering)[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const getMetadataNumber = (offering: CommerceOffering, key: string) => {
  const value = getMetadataObject(offering)[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getMetadataStringArray = (offering: CommerceOffering, key: string) => {
  const value = getMetadataObject(offering)[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

const getDisplayOrder = (offering: CommerceOffering) => getMetadataNumber(offering, 'displayOrder') ?? 999;
const getDisplayEyebrow = (offering: CommerceOffering) => getMetadataString(offering, 'displayEyebrow');
const getDisplayBadge = (offering: CommerceOffering) => getMetadataString(offering, 'displayBadge');
const getDisplaySubcopy = (offering: CommerceOffering) => getMetadataString(offering, 'displaySubcopy');
const getDisplayBullets = (offering: CommerceOffering) => getMetadataStringArray(offering, 'displayBullets');

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return 'Not available yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available yet';
  return date.toLocaleString();
};

const getPaymentLabel = (status: CommercePaymentStatus) => {
  switch (status) {
    case 'PAID':
      return 'Payment received';
    case 'PENDING':
      return 'Payment pending';
    case 'INVOICE_REQUESTED':
      return 'Invoice requested';
    case 'INVOICED':
      return 'Invoice sent';
    case 'FAILED':
      return 'Payment failed';
    case 'CANCELLED':
      return 'Payment cancelled';
    default:
      return status;
  }
};

export const getBuildRequestStatusLabel = (status: CommerceBuildRequestStatus) => {
  switch (status) {
    case 'PAID':
    case 'SCOPING':
      return 'Allocated for build';
    case 'IN_BUILD':
      return 'Final checks';
    case 'DELIVERED':
      return 'Deployed to dashboard';
    case 'INVOICE_REQUESTED':
      return 'Invoice requested';
    case 'AWAITING_BILLING':
      return 'Awaiting invoice details';
    case 'PENDING':
      return 'Pending payment';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

const EmployerBuildRequestPage: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const briefSectionRef = useRef<HTMLDivElement | null>(null);
  const [buildOfferings, setBuildOfferings] = useState<CommerceOffering[]>([]);
  const [buildRequests, setBuildRequests] = useState<CommerceBuildRequest[]>([]);
  const [creditBalance, setCreditBalance] = useState<CommerceCreditBalance | null>(null);
  const [billingProfile, setBillingProfile] = useState<CommerceBillingProfile | null>(null);
  const [selectedOfferingCode, setSelectedOfferingCode] = useState('');
  const [roleName, setRoleName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittingMode, setSubmittingMode] = useState<CommerceCheckoutMode | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setBillingProfile(null);

      try {
        const [offeringsResult, profileResult, buildRequestsResult, creditBalanceResult] = await Promise.allSettled([
          fetchBuildOfferings(api),
          fetchBillingProfile(api),
          fetchCommerceBuildRequests(api),
          fetchCreditBalance(api),
        ]);
        if (!mounted) return;

        if (offeringsResult.status === 'rejected') {
          setBuildOfferings([]);
          setSelectedOfferingCode('');
          setLoadError(getErrorMessage(offeringsResult.reason, 'Unable to load build options right now.'));
          return;
        }

        if (profileResult.status === 'fulfilled') {
          setBillingProfile(profileResult.value);
        } else {
          setBillingProfile(null);
        }
        setBuildRequests(buildRequestsResult.status === 'fulfilled' ? buildRequestsResult.value : []);
        setCreditBalance(creditBalanceResult.status === 'fulfilled' ? creditBalanceResult.value : null);

        const offerings = offeringsResult.value;
        const sortedOfferings = [...offerings].sort((left, right) => getDisplayOrder(left) - getDisplayOrder(right));
        const preferredOffering =
          sortedOfferings.find((offering) => offering.code === 'BUILD_SEMI_EXCLUSIVE') ?? sortedOfferings[0] ?? null;
        setBuildOfferings(sortedOfferings);
        setSelectedOfferingCode((current) => {
          if (current && sortedOfferings.some((offering) => offering.code === current)) {
            return current;
          }
          return preferredOffering?.code || '';
        });
      } catch (error) {
        if (!mounted) return;
        setLoadError(getErrorMessage(error, 'Unable to load build options right now.'));
        setBuildRequests([]);
        setCreditBalance(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [api]);

  const selectedOffering = useMemo(
    () => buildOfferings.find((offering) => offering.code === selectedOfferingCode) ?? null,
    [buildOfferings, selectedOfferingCode]
  );

  const handleSelectOffering = (offeringCode: string) => {
    setSelectedOfferingCode(offeringCode);
    setSubmitError(null);
    briefSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (checkoutMode: CommerceCheckoutMode) => {
    if (!selectedOffering) {
      setSubmitError('Select a build option first.');
      return;
    }
    if (!roleName.trim()) {
      setSubmitError('Role name is required.');
      return;
    }

    setSubmittingMode(checkoutMode);
    setSubmitError(null);

    try {
      if (checkoutMode === 'REQUEST_INVOICE' && !billingProfile) {
        navigate(
          buildInvoiceDetailsUrl({
            offeringCode: selectedOffering.code,
            flow: 'build',
            roleName: roleName.trim(),
            notes: notes.trim() || undefined,
          })
        );
        return;
      }

      const result = await createCommerceOrder(
        {
          offeringCode: selectedOffering.code,
          checkoutMode,
          buildRequest: {
            roleName: roleName.trim(),
            notes: notes.trim() || undefined,
          },
        },
        api
      );

      if (checkoutMode === 'PAY_NOW') {
        if (!('payfastUrl' in result) || !result.payfastUrl || !result.formFields) {
          throw new Error('Hosted payment details were not returned.');
        }
        rememberPendingCommerceOrder(result.orderId);
        submitPayFastHostedPayment(result.payfastUrl, result.formFields);
        return;
      }

      toast.success('Invoice requested.');
      navigate(`/billing/order/${result.orderId}`);
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to create the build request.');
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmittingMode(null);
    }
  };

  return (
    <EmployerCommerceShell
      activeSection="build-request"
      title="Request a build"
      description="Choose the build option that fits your role, add a short brief, and either pay now or request an invoice."
      compactHero
      actions={
        <>
          <Button variant="secondary" onClick={() => navigate('/employer/billing')}>
            Billing
          </Button>
          <Button variant="secondary" onClick={() => navigate('/employer/invoice-details')}>
            Invoice details
          </Button>
        </>
      }
    >
      {loadError ? (
        <Card className="border-[#fecaca] bg-[#fff5f5]">
          <p className="text-[14px] font-medium text-[#b91c1c]">{loadError}</p>
        </Card>
      ) : null}

      <Card className="border-[#dbe4e1] bg-white">
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="font-display text-[22px] font-bold text-[#0f172a]">Build requests</h2>
              <p className="mt-1 text-[14px] text-[#64748b]">Track paid simulation builds and deployment status.</p>
            </div>
            <div className="rounded-[12px] border border-[#dbe4e1] bg-[#fcfffe] px-4 py-3 text-sm">
              <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Available credits</div>
              <div className="mt-1 text-[18px] font-bold text-[#0f172a]">
                {creditBalance ? creditBalance.availableBalance : 'Not available'}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {buildRequests.length === 0 ? (
            <div className="rounded-[14px] border border-dashed border-[#cbd5e1] bg-[#f8fafc] px-4 py-5 text-[14px] text-[#64748b]">
              No paid build requests yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#e2e8f0] text-left text-[14px]">
                <thead className="bg-[#f8fafc] text-[12px] font-semibold uppercase tracking-[0.12em] text-[#64748b]">
                  <tr>
                    <th className="px-4 py-3">Build request</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Paid date</th>
                    <th className="px-4 py-3">Build status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {buildRequests.map((request) => (
                    <tr key={request.id}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#0f172a]">{request.roleName}</div>
                        <div className="text-[12px] text-[#64748b]">{request.offering.name}</div>
                      </td>
                      <td className="px-4 py-3 text-[#334155]">
                        {request.paymentOrder ? getPaymentLabel(request.paymentOrder.paymentStatus) : 'Not linked'}
                      </td>
                      <td className="px-4 py-3 text-[#334155]">{formatDateTime(request.paymentOrder?.paidAt)}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-[#f0fdfa] px-3 py-1 text-[12px] font-semibold text-[#0f766e]">
                          {getBuildRequestStatusLabel(request.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {!billingProfile ? (
        <Card className="border-[#dbe4e1] bg-[#fcfffe]">
          <p className="text-[14px] text-[#475569]">
            Pay now can start immediately. If you choose Request invoice, we will ask for invoice details only when needed.
          </p>
        </Card>
      ) : null}

      <Card className="border-[#dbe4e1] !p-3 sm:!p-4">
        <div className="grid items-stretch gap-5 xl:grid-cols-3">
          {loading ? <p className="text-[14px] text-[#64748b]">Loading build options...</p> : null}
          {!loading && buildOfferings.length === 0 ? (
            <div className="rounded-[14px] border border-dashed border-[#cbd5e1] bg-[#f8fafc] px-4 py-5 text-[14px] text-[#64748b]">
              No build options are available right now.
            </div>
          ) : null}

          {buildOfferings.map((offering) => {
            const selected = offering.code === selectedOfferingCode;
            const highlighted = offering.code === 'BUILD_SEMI_EXCLUSIVE';
            const eyebrow = getDisplayEyebrow(offering);
            const badge = getDisplayBadge(offering);
            const subcopy = getDisplaySubcopy(offering);
            const bullets = getDisplayBullets(offering);

            return (
              <div
                key={offering.id}
                className={`relative grid h-full min-h-[680px] grid-rows-[44px_96px_64px_120px_1fr_auto] rounded-[22px] border p-6 transition-all ${
                  selected
                    ? 'border-[#0d9488] bg-[#f0fdfa] shadow-[0_22px_55px_rgba(13,148,136,0.18)]'
                    : highlighted
                      ? 'border-[#99f6e4] bg-[linear-gradient(180deg,#ffffff_0%,#f0fdfa_100%)] shadow-[0_20px_48px_rgba(13,148,136,0.16)]'
                      : 'border-[#dbe4e1] bg-white shadow-[0_16px_36px_rgba(15,23,42,0.08)]'
                }`}
              >
                <div className="flex min-h-[28px] items-start justify-between gap-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0d9488]">
                    {eyebrow || 'BUILD OPTION'}
                  </p>
                  {badge ? (
                    <span className="rounded-full bg-[#0d9488] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
                      {badge}
                    </span>
                  ) : (
                    <span className="invisible rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em]">
                      MOST REQUESTED
                    </span>
                  )}
                </div>

                <div className="flex items-start pt-6">
                  <p className="text-[28px] font-bold leading-tight text-[#0f172a]">{offering.name}</p>
                </div>

                <div className="flex items-start">
                  <p className="text-[40px] font-bold leading-none text-[#0d9488]">
                    {formatHeroPrice(offering.priceZar)}
                  </p>
                </div>

                <div className="flex items-start">
                  <p className="text-[14px] leading-6 text-[#475569]">{subcopy}</p>
                </div>

                <ul className="space-y-3 text-[14px] leading-6 text-[#334155]">
                  {bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3">
                      <span className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-[#0d9488]" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-6">
                  <Button
                    type="button"
                    onClick={() => handleSelectOffering(offering.code)}
                    className={
                      selected
                        ? 'w-full'
                        : 'w-full border border-[#0d9488] bg-white text-[#0d9488] hover:bg-[#f0fdfa]'
                    }
                  >
                    Request a build
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div ref={briefSectionRef}>
        <Card className="border-[#dbe4e1]">
          <CardHeader>
            <h2 className="font-display text-[24px] font-bold text-[#0f172a]">Short brief</h2>
            <p className="mt-1 text-[14px] text-[#64748b]">A role name is required. Notes are optional.</p>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <Label htmlFor="roleName">Role name</Label>
                <Input
                  id="roleName"
                  value={roleName}
                  onChange={(event) => setRoleName(event.target.value)}
                  placeholder="e.g. Commercial Analyst"
                  required
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Anything useful for the build"
                  rows={6}
                />
              </div>

              {submitError ? <p className="text-[13px] font-medium text-[#b91c1c]">{submitError}</p> : null}

              <div className="flex flex-wrap gap-3">
                <Button onClick={() => handleSubmit('PAY_NOW')} disabled={submittingMode !== null}>
                  {submittingMode === 'PAY_NOW' ? 'Redirecting...' : 'Pay now'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleSubmit('REQUEST_INVOICE')}
                  disabled={submittingMode !== null}
                >
                  {submittingMode === 'REQUEST_INVOICE' ? 'Starting...' : 'Request invoice'}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </EmployerCommerceShell>
  );
};

export default EmployerBuildRequestPage;
